// Formularios de los datos de cobro de un holding. Funciones puras.
//
// Los datos viven en dos servicios, cada uno dueño de lo suyo:
//   · microservice-contracts: la identidad fiscal (giro y dirección fiscal con comuna), con
//     PATCH contracts/holdings/:id/fiscal-profile. Todos los campos son obligatorios y el DTO
//     rechaza cualquier otro (whitelist + forbidNonWhitelisted).
//   · microservice-billing: el perfil de cobro (contacto, correos, teléfono y plazo), con PUT
//     billing/collections/holding-profiles/:holdingId, que es PARCIAL.
// Por eso son dos formularios con su propio "Guardar": un fallo en uno no deja al otro a medias.

import { validateRuleForm } from './commerceRuleForm';

// Límites de los DTOs (contracts: UpdateHoldingFiscalProfileDto; billing:
// SaveHoldingCollectionProfileDto). Se avisan en el campo; el servidor revalida.
export const MAX_ACTIVITY_LENGTH = 200;
export const MAX_STREET_LENGTH = 200;
export const MAX_DIVISION_LENGTH = 100;
export const MAX_CONTACT_NAME_LENGTH = 120;

const trim = (value) => String(value ?? '').trim();

// ── Datos fiscales (contracts) ───────────────────────────────────────────────────────────

/** Estado del formulario fiscal desde el holding de GET contracts/holdings. */
export function fiscalToForm(holding) {
  const address = holding?.fiscalAddress ?? {};
  return {
    activity: holding?.activity ?? '',
    street: address.street ?? '',
    state: address.state ?? '',
    municipality: address.municipality ?? '',
    city: address.city ?? '',
  };
}

/**
 * Errores por campo (claves i18n). Todo es obligatorio salvo la ciudad: sin giro ni dirección
 * completa la nota del holding no se puede emitir.
 */
export function validateFiscalForm(form) {
  const errors = {};
  const required = (field, max) => {
    const value = trim(form[field]);
    if (!value) errors[field] = 'Este dato es obligatorio.';
    else if (value.length > max) errors[field] = 'Es demasiado largo.';
  };
  required('activity', MAX_ACTIVITY_LENGTH);
  required('street', MAX_STREET_LENGTH);
  required('state', MAX_DIVISION_LENGTH);
  required('municipality', MAX_DIVISION_LENGTH);
  if (trim(form.city).length > MAX_DIVISION_LENGTH) errors.city = 'Es demasiado largo.';
  return errors;
}

/**
 * Body del PATCH fiscal-profile. `state` y `municipality` van como NOMBRE (el receptor del PDF
 * los imprime tal cual): `resolved` trae el nombre del catálogo cuando el valor guardado era un
 * código o venía con otra grafía. La ciudad solo viaja si tiene texto: ausente conserva la
 * guardada, y vacía el DTO la rechaza. El país no se toca desde acá.
 *
 * @param {ReturnType<typeof fiscalToForm>} form
 * @param {{ state?: string|null, municipality?: string|null }} [resolved]
 */
export function buildFiscalPayload(form, resolved = {}) {
  const city = trim(form.city);
  return {
    activity: trim(form.activity),
    fiscalAddress: {
      street: trim(form.street),
      state: trim(resolved.state || form.state),
      municipality: trim(resolved.municipality || form.municipality),
      ...(city ? { city } : {}),
    },
  };
}

/** ¿Cambió algo respecto de lo cargado? */
export const fiscalFormDirty = (form, initial) =>
  ['activity', 'street', 'state', 'municipality', 'city'].some(
    (field) => trim(form[field]) !== trim(initial[field]),
  );

// ── Perfil de cobro (billing) ────────────────────────────────────────────────────────────

/**
 * Modo del plazo: '' = todavía no se eligió (la primera emisión lo exige), 'inherit' = hereda
 * el plazo general del operador, 'own' = plazo propio.
 */
export function profileToForm(profile) {
  const days = profile?.paymentTermDays;
  const chosen = profile?.paymentTermChosen === true;
  let termMode = '';
  if (chosen) termMode = Number.isInteger(days) ? 'own' : 'inherit';
  return {
    contactName: profile?.contactName ?? '',
    contactEmails: Array.isArray(profile?.contactEmails) ? [...profile.contactEmails] : [],
    contactPhone: profile?.contactPhone ?? '',
    termMode,
    termDays: Number.isInteger(days) ? String(days) : '',
  };
}

/** Errores por campo (claves i18n): `{ contactName?, termDays?, contactPhone? }`. */
export function validateProfileForm(form) {
  // Mismas reglas que la regla de un comercio para el plazo y el teléfono.
  const { termDays, contactPhone } = validateRuleForm({
    ownTerm: form.termMode === 'own',
    termDays: form.termDays,
    contactPhone: form.contactPhone,
  });
  const errors = {};
  if (trim(form.contactName).length > MAX_CONTACT_NAME_LENGTH) {
    errors.contactName = 'Es demasiado largo.';
  }
  if (termDays) errors.termDays = termDays;
  if (contactPhone) errors.contactPhone = contactPhone;
  return errors;
}

const sameList = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

/**
 * Body PARCIAL del PUT: solo lo que cambió. El plazo viaja cuando se eligió o cambió: mandarlo
 * (con días o con `null` = heredar) es lo que billing registra como "plazo elegido".
 *
 * @returns {object} vacío si no hay cambios
 */
export function buildProfilePayload(form, initial) {
  const payload = {};
  const nameNow = trim(form.contactName);
  if (nameNow !== trim(initial.contactName)) payload.contactName = nameNow || null;
  if (!sameList(form.contactEmails, initial.contactEmails)) {
    payload.contactEmails = [...form.contactEmails];
  }
  const phoneNow = trim(form.contactPhone);
  if (phoneNow !== trim(initial.contactPhone)) payload.contactPhone = phoneNow || null;
  const termNow = form.termMode === 'own' ? Number(form.termDays) : null;
  const termBefore = initial.termMode === 'own' ? Number(initial.termDays) : null;
  if (form.termMode && (form.termMode !== initial.termMode || termNow !== termBefore)) {
    payload.paymentTermDays = termNow;
  }
  return payload;
}
